export type RunMatchPreview = {
  where: 'input' | 'error' | 'notes' | 'id' | 'node' | string;
  node_id?: string | null;
  field?: 'input' | 'output' | 'error' | string | null;
  snippet: string;
};

export type HighlightPart = { text: string; match: boolean };

const SKIP_STRING_PREFIXES = ['data:'];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function normalizeRunSearch(query: string | undefined | null): string {
  return (query || '').trim();
}

export function isRunSearchActive(query: string | undefined | null): boolean {
  return normalizeRunSearch(query).length >= 2;
}

function shouldSkipString(value: string): boolean {
  if (!value) return true;
  return SKIP_STRING_PREFIXES.some((prefix) => value.startsWith(prefix) && value.length > 200);
}

export function valueContains(value: unknown, query: string, depth = 0): boolean {
  const q = normalizeRunSearch(query).toLowerCase();
  if (!q || depth > 12) return false;
  if (typeof value === 'string') {
    if (shouldSkipString(value)) return false;
    return value.toLowerCase().includes(q);
  }
  if (Array.isArray(value)) {
    return value.some((item) => valueContains(item, q, depth + 1));
  }
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some((item) =>
      valueContains(item, q, depth + 1)
    );
  }
  if (value == null || typeof value === 'boolean') return false;
  return String(value).toLowerCase().includes(q);
}

export function nodeMatchesQuery(nodeResult: unknown, query: string): boolean {
  if (!isRunSearchActive(query) || !nodeResult) return false;
  if (typeof nodeResult !== 'object') return valueContains(nodeResult, query);
  const result = nodeResult as Record<string, unknown>;
  return (
    valueContains(result.input, query) ||
    valueContains(result.output, query) ||
    valueContains(result.error, query)
  );
}

export function summarizeInput(input: unknown, maxLen = 96): string {
  if (input == null) return '';
  if (typeof input === 'string') {
    const compact = input.trim().replace(/\s+/g, ' ');
    return compact.length > maxLen ? `${compact.slice(0, maxLen)}…` : compact;
  }

  const parts: string[] = [];
  const walk = (value: unknown, depth: number) => {
    if (parts.join(' · ').length >= maxLen || depth > 5) return;
    if (typeof value === 'string') {
      const compact = value.trim().replace(/\s+/g, ' ');
      if (!compact || shouldSkipString(compact)) return;
      if (/^https?:\/\//i.test(compact) && compact.length > 80) return;
      parts.push(compact.slice(0, 80));
      return;
    }
    if (Array.isArray(value)) {
      value.slice(0, 8).forEach((item) => walk(item, depth + 1));
      return;
    }
    if (value && typeof value === 'object') {
      Object.values(value as Record<string, unknown>).forEach((item) => walk(item, depth + 1));
    }
  };
  walk(input, 0);
  const joined = parts.join(' · ');
  if (!joined) return '';
  return joined.length > maxLen ? `${joined.slice(0, maxLen)}…` : joined;
}

export function splitHighlight(text: string, query: string, maxParts = 80): HighlightPart[] {
  const q = normalizeRunSearch(query);
  if (!q || q.length < 2) return [{ text, match: false }];
  const re = new RegExp(`(${escapeRegExp(q)})`, 'gi');
  const parts: HighlightPart[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (parts.length >= maxParts - 1) break;
    if (match.index > last) {
      parts.push({ text: text.slice(last, match.index), match: false });
    }
    parts.push({ text: match[0], match: true });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parts.push({ text: text.slice(last), match: false });
  }
  return parts.length > 0 ? parts : [{ text, match: false }];
}

export function formatMatchWhere(preview: RunMatchPreview): string {
  if (preview.where === 'input') return 'Input';
  if (preview.where === 'error') return 'Error';
  if (preview.where === 'notes') return 'Notes';
  if (preview.where === 'id') return 'Run ID';
  if (preview.where === 'node') {
    const node = preview.node_id || 'step';
    if (preview.field === 'input') return `${node} input`;
    if (preview.field === 'output') return `${node} output`;
    if (preview.field === 'error') return `${node} error`;
    return node;
  }
  return preview.where;
}

export function firstMatchingNodeId(
  previews: RunMatchPreview[] | undefined | null,
  nodeResults?: Record<string, unknown> | null,
  query?: string
): string | null {
  const fromPreview = previews?.find((item) => item.node_id)?.node_id;
  if (fromPreview) return fromPreview;
  if (!nodeResults || !isRunSearchActive(query)) return null;
  for (const [nodeId, result] of Object.entries(nodeResults)) {
    if (nodeMatchesQuery(result, query || '')) return nodeId;
  }
  return null;
}
